import {
  ProductRepository,
  CategoryRepository,
  BarcodeRepository,
  OptionalGroupRepository,
  VariationRepository
} from "core/repository";
import { Base2Presenter } from "core/presenter";
import { Sellable } from "core/entity";
import _ from "lodash";

export default class ProductInfoPresenter extends Base2Presenter {
  initialState() {
    return {
      title: "",
      loading: false,
      errorable: {
        visible: false,
        content: ""
      },
      deleteDialog: {
        visible: false,
        uuid: "",
        name: "",
        code: "",
        companyUuid: ""
      },
      product: new Sellable(),
      productUuid: "",
      linkedOptionalGroups: [],
      selectedVariations: [],
      variationsListOptions: [],
      variationsList: [],
      skuLoading: false,
      skuPagination: {
        perPage: 25,
        currentPage: 1,
        total: 0
      },
      company: {
        features: []
      },
      skuOrder: {
        field: "name",
        direction: "ASC"
      },
      hasSku: false,
      skuResultSet: []
    };
  }

  async onStart() {
    let productUuid = this.router.stateService.params.productUuid;
    const currentUser = await this.mRuntime.get("currentUser");

    await this.enqueue({ productUuid, company: currentUser.company });
    await this.loadProduct();
  }

  async loadProduct() {
    this.enqueue({ loading: true });
    const result = await ProductRepository.getOnline(
      this.viewModel.productUuid
    );

    if (result.success) {
      const product = result.data;

      const categoryUuid =
        product.category_uuid === "" ? null : product.category_uuid;
      const categoryResults = await CategoryRepository.getOnline(categoryUuid);
      const category =
        categoryResults.success && !categoryResults.data.deleted
          ? categoryResults.data.name
          : "Sem categoria";

      await this.loadBarcodes(product);
      await this.loadOptionalGroups(product);
      await this.loadProductVariations(product);
      this.loadSkus();

      product.categoryName = category;

      this.enqueue({ title: product.name, product });
    } else {
      this.sendNotification({
        type: "error",
        title: "Oooops...",
        content:
          "Não conseguimos carregar este produto! Tente novamente em alguns minutos."
      });
    }

    this.enqueue({ loading: false });
  }

  async loadBarcodes(product) {
    const result = await BarcodeRepository.getListOnline({
      order: [
        {
          field: "code",
          direction: "ASC"
        }
      ],
      pagination: {
        page: 1,
        perPage: 25
      },
      filters: [
        { field: "sellable_uuid", value: product.uuid, operation: "EQ" }
      ]
    });

    if (result.success) {
      product.barcodes = result.data;
    } else if (result.fatal) {
      this.sendNotification({
        type: "error",
        title: "Oooops...",
        content:
          "Ocorreu um erro fatal ao se conectar com o servidor. Tente novamente mais tarde."
      });
    } else {
      this.sendNotification({
        type: "error",
        title: "Oooops...",
        content: "Não foi possível carregar os dados."
      });
    }
  }

  async loadOptionalGroups(product) {
    const optionalGroupsUuids = product.optional_groups_uuids || [];
    const optionalGroups = await this.getOptionalGroups();
    const linkedOptionalGroups = [];

    optionalGroupsUuids.forEach(opt => {
      const linkedOptionalGroup = optionalGroups.filter(optGroup => {
        return optGroup.uuid === opt;
      });

      if (linkedOptionalGroup.length) {
        linkedOptionalGroups.push(linkedOptionalGroup[0]);
      }
    });

    this.enqueue({ linkedOptionalGroups });
  }

  async getOptionalGroupsFilters() {
    const currentUser = await this.mRuntime.get("currentUser");
    const companyUuid = currentUser.company_uuid;
    return [
      // { connector: 'AND', field: 'enabled', operation: 'EQ', value: 'true' }, commented so it show disabled optGroups
      {
        connector: "AND",
        field: "company_uuid",
        operation: "EQ",
        value: companyUuid
      },
      { connector: "AND", field: "deleted", operation: "EQ", value: "false" }
    ];
  }

  async getOptionalGroups() {
    this.enqueue({ loading: true });

    const filters = await this.getOptionalGroupsFilters();
    const pagination = { page: 1 /* , perPage: 25 */ };
    const order = [{ field: "name", direction: "ASC" }];

    const repositoryResult = await OptionalGroupRepository.getListOnline({
      order,
      pagination,
      filters
    });

    if (repositoryResult.success) {
      this.enqueue({ loading: false });
      return repositoryResult.data;
    } else if (repositoryResult.fatal) {
      this.sendNotification({
        type: "error",
        title: "Oops...",
        content:
          "Nosso serviço encontra-se indisponível. Tente novamente mais tarde."
      });
    } else {
      this.sendNotification({
        type: "error",
        title: "Oops...",
        content:
          "Não foi possível carregar os grupos de opcionais. Tente novamente mais tarde."
      });
    }

    this.enqueue({ loading: false });
  }

  async onRequestDelete() {
    const { product } = this.viewModel;

    this.sendNotification({
      type: "confirmation",
      title: "Você tem certeza?",
      content: `Tem certeza que deseja excluir o product ${product.identifier()}?`,
      actions: [
        {
          label: "Voltar"
        },
        {
          label: "Sim, quero excluir",
          type: "accent",
          callback: () => this.doDelete()
        }
      ]
    });
  }

  async doDelete() {
    this.enqueue({ loading: false });

    const result = await ProductRepository.delete(this.viewModel.product.uuid);

    if (result.success) {
      this.sendNotification({
        type: "success",
        content: "O produto foi excluído."
      });
      this.onGoBack();
    } else {
      this.sendNotification({
        type: "error",
        title: "Oops...",
        content: result.error || "Não foi possível excluir o produto."
      });
    }

    this.enqueue({ loading: false });
  }

  async loadProductVariations(sellable) {
    this.enqueue({ loading: true });

    const order = [{ field: "name", direction: "ASC" }];
    const pagination = { page: 1 };

    const filters = [
      { connector: "AND", field: "deleted", operation: "NOT", value: true }
    ];
    const repositoryResult = await VariationRepository.getListOnline({
      order,
      pagination,
      filters
    });

    if (repositoryResult.success) {
      const variationsList = _.map(repositoryResult.data, variation => {
        return {
          name: variation.name,
          uuid: variation.uuid,
          variation_options: variation.variation_options
        };
      });
      const fullVariationsListOptions = _.map(variationsList, variation => {
        return { value: variation.uuid, label: variation.name };
      });

      this.enqueue({
        variationsList,
        variationsListOptions: fullVariationsListOptions
      });

      if (sellable && sellable.variations_uuids) {
        this.loadSelectedVariations(variationsList, sellable);
      }
    } else if (!repositoryResult.userCancelled) {
      this.sendNotification({
        type: "error",
        title: "Ooops...",
        content:
          "Houve um problema ao buscar as variações.\nTente novamente mais tarde ou entre em contato com o suporte."
      });
    }

    this.enqueue({ loading: false });
  }

  loadSelectedVariations(variationsList, sellable) {
    const variationsUuid = sellable.variations_uuids || [];
    const selectedVariations = [];

    variationsList.forEach(variation => {
      if (variationsUuid.includes(variation.uuid)) {
        const foundVariation = {
          name: variation.name,
          uuid: variation.uuid,
          variation_options: variation.variation_options
        };
        selectedVariations.push(foundVariation);
      }
    });

    this.enqueue({ selectedVariations });
  }

  async loadSkus() {
    const repositoryResult = await ProductRepository.getListOnline({
      order: [this.viewModel.skuOrder],
      pagination: {
        page: this.viewModel.skuPagination.currentPage,
        perPage: this.viewModel.skuPagination.perPage
      },
      filters: await this.getSkuFilters()
    });

    if (repositoryResult.success) {
      // Required to load barcode of each product

      if (repositoryResult.data.length) {
        this.enqueue({ hasSku: true });
        // backEnd should do it, front can only calculate page per page
        const stockCountResponse = await ProductRepository.getSkuStock(
          this.viewModel.product.uuid
        );
        if (stockCountResponse.success) {
          const stock_count = stockCountResponse.data.stock_count;
          let { product } = this.viewModel;
          product.stock_count = stock_count;
          this.enqueue({ product });
        }
      }

      await this.loadEachSkuData(repositoryResult);
    } else if (!repositoryResult.userCancelled) {
      this.sendNotification({
        type: "error",
        title: "Ocorreu um erro",
        content:
          "Não foi possível carregar os dados. Tente novamente mais tarde."
      });
    }
  }

  async getSkuFilters() {
    const productUuid = await this.router.stateService.params.productUuid;

    const filters = [
      { connector: "AND", field: "type", operation: "EQ", value: "sku" },
      {
        connector: "AND",
        field: "sku_uuid",
        operation: "EQ",
        value: productUuid
      }
    ];

    return filters;
  }

  async loadEachSkuData(repositoryResultSkus) {
    this.enqueue({ skuLoading: true });

    const repoData = repositoryResultSkus.data;

    const { skuResultSet } = this.viewModel;

    for (const [index, sku] of repoData.entries()) {
      const barcodesRepo = await this.getBarcodes(sku.uuid);

      const result = {
        uuid: sku.uuid,
        code: sku.code,
        barcodes: [],
        name: sku.name,
        stock_count: sku.stock_count || "0.0",
        sell_value: sku.sell_value,
        deleted: sku.deleted,
        company_uuid: sku.company_uuid,
        resultSetIndex: index,
        edited: false
      };

      if (barcodesRepo.success) {
        result.barcodes = barcodesRepo.data;
      }

      skuResultSet.push(result);
    }
    let { skuPagination } = this.viewModel;
    skuPagination.total = repositoryResultSkus.total;
    skuPagination.perPage = repositoryResultSkus.perPage;

    this.enqueue({
      skuLoading: false,
      skuResultSet,
      skuPagination
    });
  }

  async getBarcodes(productUuid) {
    return await BarcodeRepository.getListOnline({
      order: [{ field: "code", direction: "ASC" }],
      pagination: { page: 1 },
      filters: [{ field: "sellable_uuid", value: productUuid, operation: "EQ" }]
    });
  }

  onGoBack() {
    this.navigateTo("app.registration.product");
  }

  onEdit() {
    this.navigateTo("app.registration.product.edit", {
      productUuid: this.viewModel.product.uuid
    });
  }
}



// WEBPACK FOOTER //
// ./src/components/app/registration/product/info/presenter.js